<template>
    <Modal
        v-model:open="visible"
        title="分類管理"
        :width="600"
        :footer="null"
        @ok="handleOk"
        @cancel="handleCancel"
    >
        <div class="category-management">
            <!-- 分類列表 -->
            <div class="category-list">
                <div
                    v-for="category in categories"
                    :key="category.id"
                    class="category-item"
                >
                    <div class="category-info">
                        <div class="category-header">
                            <a-tag
                                v-if="category.id != null"
                                :color="getCategoryColor(category.id)"
                                class="category-color-tag"
                            >
                                {{ category.name }}
                            </a-tag>
                            <span v-else class="category-name">
                                {{ category.name }}
                            </span>
                            <span class="category-code">
                                {{ category.category }}
                            </span>
                        </div>
                        <div class="category-meta">
                            <span class="category-count">
                                {{ category.mission_count || 0 }} 個任務
                            </span>
                        </div>
                    </div>
                    <a-space class="category-actions">
                        <a-button
                            v-if="canEdit"
                            type="text"
                            size="small"
                            @click="handleEdit(category)"
                        >
                            <template #icon><EditOutlined /></template>
                        </a-button>
                        <a-button
                            v-if="
                                canDelete && (category.mission_count || 0) === 0
                            "
                            type="text"
                            size="small"
                            danger
                            @click="handleDelete(category)"
                        >
                            <template #icon><DeleteOutlined /></template>
                        </a-button>
                    </a-space>
                </div>
                <div v-if="categories.length === 0" class="empty-state">
                    <a-empty description="尚無分類" :image="false" />
                </div>
            </div>

            <!-- 新增/編輯表單 -->
            <div v-if="editingCategory" class="category-form">
                <a-divider>
                    {{ editingCategory.id ? '編輯' : '新增' }}分類
                </a-divider>
                <a-form
                    ref="formRef"
                    :model="formData"
                    :rules="rules"
                    :label-col="{ span: 6 }"
                    :wrapper-col="{ span: 18 }"
                >
                    <a-form-item label="分類代碼" name="category">
                        <a-input
                            v-model:value="formData.category"
                            placeholder="例如：mainline"
                        />
                    </a-form-item>
                    <a-form-item label="分類名稱" name="name">
                        <a-input
                            v-model:value="formData.name"
                            placeholder="例如：主線任務"
                        />
                    </a-form-item>
                    <a-form-item :wrapper-col="{ offset: 6, span: 18 }">
                        <a-space>
                            <a-button type="primary" @click="handleSubmit">
                                儲存
                            </a-button>
                            <a-button @click="handleCancelEdit">取消</a-button>
                        </a-space>
                    </a-form-item>
                </a-form>
            </div>

            <!-- 新增按鈕 -->
            <div v-else class="add-button-container">
                <a-button
                    v-if="canCreate"
                    type="dashed"
                    block
                    @click="handleAdd"
                    class="add-category-btn"
                >
                    <template #icon><PlusOutlined /></template>
                    新增分類
                </a-button>
            </div>

            <!-- 關閉按鈕 -->
            <div class="modal-footer">
                <a-button @click="handleCancel">關閉</a-button>
            </div>
        </div>
    </Modal>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { message, Modal as AntModal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined
} from '@ant-design/icons-vue';
import Modal from '@/components/common/Modal.vue';
import {
    getMissionCategories,
    createMissionCategory,
    updateMissionCategory,
    deleteMissionCategory
} from '@/api/missions';
import { handleHttpError } from '@/api/errorHandler';
import { useAdminInfo } from '@/stores/useAdminInfo';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:visible', 'success']);

const visible = computed({
    get: () => props.visible,
    set: (val) => emit('update:visible', val)
});

const adminStore = useAdminInfo();

// 權限檢查
const canCreate = computed(() => adminStore.hasPermissionCode('130101')); // 新增任務
const canEdit = computed(() => adminStore.hasPermissionCode('130102')); // 修改任務
const canDelete = computed(() => adminStore.hasPermissionCode('130103')); // 刪除任務

const categories = ref([]);
const editingCategory = ref(null);
const formRef = ref(null);
const loading = ref(false);

const formData = ref({
    category: '',
    name: ''
});

const rules = {
    category: [{ required: true, message: '請輸入分類代碼', trigger: 'blur' }],
    name: [{ required: true, message: '請輸入分類名稱', trigger: 'blur' }]
};

// 分類標籤顏色（與任務設置樹狀列表一致，飽和度足以便白字可讀）
const CATEGORY_COLORS = [
    '#2a4a7a', // 霓虹紫
    '#3b6ea5', // 天空藍
    '#e85a9c', // 深粉
    '#64748b', // 日落粉
    '#ffa87a', // 日落橘
    '#6b8ce8', // 深藍
    '#b86ef0', // 紫羅蘭
    '#5eb4c4' // 青藍
];
const getCategoryColor = (categoryId) => {
    if (categoryId == null || categoryId === '') return 'gray';
    const id = Number(categoryId);
    if (Number.isNaN(id)) return 'gray';
    const index = Math.abs(id) % CATEGORY_COLORS.length;
    return CATEGORY_COLORS[index] || 'gray';
};

// 載入分類列表
const loadCategories = async () => {
    try {
        loading.value = true;
        const response = await getMissionCategories();
        if (response.result || response.success) {
            categories.value = response.data || [];
        } else {
            categories.value = [];
        }
    } catch (error) {
        handleHttpError(error, '載入分類列表');
        categories.value = [];
    } finally {
        loading.value = false;
    }
};

// 監聽 visible 變化，開啟時載入資料
watch(
    () => props.visible,
    (newVal) => {
        if (newVal) {
            loadCategories();
            editingCategory.value = null;
            formData.value = { category: '', name: '' };
        }
    }
);

// 新增分類
const handleAdd = () => {
    editingCategory.value = {};
    formData.value = { category: '', name: '' };
    nextTick(() => {
        formRef.value?.clearValidate();
    });
};

// 編輯分類
const handleEdit = (category) => {
    editingCategory.value = { ...category };
    formData.value = {
        category: category.category || '',
        name: category.name || ''
    };
    nextTick(() => {
        formRef.value?.clearValidate();
    });
};

// 取消編輯
const handleCancelEdit = () => {
    editingCategory.value = null;
    formData.value = { category: '', name: '' };
};

// 提交表單
const handleSubmit = async () => {
    try {
        await formRef.value.validate();
        loading.value = true;

        if (editingCategory.value.id) {
            // 更新
            await updateMissionCategory(
                editingCategory.value.id,
                formData.value
            );
            message.success('更新分類成功');
        } else {
            // 新增
            await createMissionCategory(formData.value);
            message.success('新增分類成功');
        }

        await loadCategories();
        editingCategory.value = null;
        formData.value = { category: '', name: '' };
        emit('success');
    } catch (error) {
        if (error.errorFields) {
            // 表單驗證錯誤
            return;
        }
        handleHttpError(
            error,
            editingCategory.value.id ? '更新分類' : '新增分類'
        );
    } finally {
        loading.value = false;
    }
};

// 刪除分類
const handleDelete = (category) => {
    if ((category.mission_count || 0) > 0) {
        message.warning('該分類下仍有任務，無法刪除');
        return;
    }

    AntModal.confirm({
        title: '確認刪除',
        content: `確定要刪除分類「${category.name}」嗎？`,
        okText: '確定',
        cancelText: '取消',
        okType: 'danger',
        onOk: async () => {
            try {
                loading.value = true;
                await deleteMissionCategory(category.id);
                message.success('刪除分類成功');
                await loadCategories();
                emit('success');
            } catch (error) {
                handleHttpError(error, '刪除分類');
            } finally {
                loading.value = false;
            }
        }
    });
};

// Modal 確認（當 Modal 有 footer 時才會觸發，我們設為 null 所以不會觸發）
const handleOk = () => {
    // 此函數不會被調用，因為我們設置了 footer: null
};

// Modal 取消
const handleCancel = () => {
    editingCategory.value = null;
    formData.value = { category: '', name: '' };
    visible.value = false;
};
</script>

<style scoped>
.category-management {
    padding: 8px 0;
}

.category-list {
    margin-bottom: 24px;
    max-height: 400px;
    overflow-y: auto;
    padding-right: 4px;
}

/* 自定義滾動條 */
.category-list::-webkit-scrollbar {
    width: 6px;
}

.category-list::-webkit-scrollbar-track {
    background: rgba(42, 74, 122, 0.1);
    border-radius: 3px;
}

.category-list::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5);
    border-radius: 3px;
}

.category-list::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(135deg, #1a4268, #3b6ea5);
}

.category-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    margin-bottom: 12px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
}

.category-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(42, 74, 122, 0.1),
        transparent
    );
    transition: left 0.5s ease;
}

.category-item:hover::before {
    left: 100%;
}

.category-item:hover {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 1),
        rgba(248, 249, 250, 0.95)
    );
    border-color: rgba(42, 74, 122, 0.5);
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.2),
        0 0 20px rgba(42, 74, 122, 0.1);
    transform: translateY(-2px);
}

.category-info {
    flex: 1;
    min-width: 0;
}

.category-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
    flex-wrap: wrap;
}

.category-name {
    font-size: 15px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.3px;
}

.category-code {
    padding: 4px 10px;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.15),
        rgba(138, 175, 255, 0.15)
    );
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 6px;
    color: #2a4a7a;
    font-weight: 500;
    font-size: 12px;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 4px rgba(42, 74, 122, 0.1);
    font-family: 'Courier New', monospace;
}

.category-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    font-family: 'Courier New', monospace;
}

.category-count {
    color: #8a8a9a;
    font-weight: 500;
    padding: 2px 0;
}

.category-actions {
    flex-shrink: 0;
}

.category-actions :deep(.ant-btn) {
    border-radius: 6px;
    transition: all 0.2s ease;
}

.category-actions :deep(.ant-btn-text:hover) {
    background: rgba(42, 74, 122, 0.1) !important;
    color: #2a4a7a !important;
}

.category-actions :deep(.ant-btn-dangerous.ant-btn-text:hover) {
    background: rgba(255, 77, 79, 0.1) !important;
    color: #ff4d4f !important;
}

.empty-state {
    padding: 60px 0;
    text-align: center;
}

.empty-state :deep(.ant-empty-description) {
    color: #8a8a9a;
    font-family: 'Courier New', monospace;
}

.category-form {
    margin-top: 20px;
    padding: 20px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.category-form :deep(.ant-divider) {
    margin: 0 0 20px 0;
    border-color: rgba(42, 74, 122, 0.3);
}

.category-form :deep(.ant-divider-inner-text) {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 14px;
    background: linear-gradient(135deg, #2a4a7a, #3b6ea5);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    padding: 0 12px;
}

.add-button-container {
    margin-top: 16px;
}

.add-category-btn {
    height: 44px !important;
    border: 2px dashed rgba(42, 74, 122, 0.4) !important;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    ) !important;
    color: #2a4a7a !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 14px !important;
    border-radius: 10px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    position: relative;
    overflow: hidden;
}

.add-category-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(42, 74, 122, 0.2),
        transparent
    );
    transition: left 0.5s ease;
}

.add-category-btn:hover::before {
    left: 100%;
}

.add-category-btn:hover {
    border-color: rgba(42, 74, 122, 0.7) !important;
    color: #1a4268 !important;
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.1)
    ) !important;
    box-shadow:
        0 4px 12px rgba(42, 74, 122, 0.3),
        inset 0 0 20px rgba(42, 74, 122, 0.1) !important;
    transform: translateY(-2px);
}

.add-category-btn:active {
    transform: translateY(0);
}

:deep(.ant-form-item-label > label) {
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    color: #2a2a3a !important;
}

:deep(.ant-input) {
    font-family: 'Courier New', monospace !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    background: rgba(255, 255, 255, 0.95) !important;
}

:deep(.ant-input:hover) {
    border-color: rgba(42, 74, 122, 0.5) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.1) !important;
}

:deep(.ant-input:focus),
:deep(.ant-input-focused) {
    border-color: #2a4a7a !important;
    box-shadow:
        0 0 0 2px rgba(42, 74, 122, 0.2),
        0 2px 8px rgba(42, 74, 122, 0.15) !important;
}

:deep(.ant-form-item) {
    margin-bottom: 20px;
}

.category-form :deep(.ant-btn-primary) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    border: none !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    height: 36px !important;
    padding: 0 20px !important;
    box-shadow:
        0 4px 12px rgba(42, 74, 122, 0.4),
        inset 0 0 10px rgba(255, 255, 255, 0.1) !important;
    transition: all 0.3s ease !important;
}

.category-form :deep(.ant-btn-primary:hover) {
    background: linear-gradient(45deg, #1a4268, #3b6ea5) !important;
    box-shadow:
        0 6px 20px rgba(42, 74, 122, 0.6),
        inset 0 0 15px rgba(255, 255, 255, 0.15) !important;
    transform: translateY(-2px);
}

.category-form :deep(.ant-btn:not(.ant-btn-primary)) {
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    height: 36px !important;
    padding: 0 20px !important;
    transition: all 0.3s ease !important;
}

.category-form :deep(.ant-btn:not(.ant-btn-primary):hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.05) !important;
}

.modal-footer {
    margin-top: 24px;
    padding-top: 20px;
    border-top: 1px solid rgba(42, 74, 122, 0.2);
    display: flex;
    justify-content: flex-end;
}

.modal-footer :deep(.ant-btn) {
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    height: 36px !important;
    padding: 0 20px !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    transition: all 0.3s ease !important;
}

.modal-footer :deep(.ant-btn:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.05) !important;
}
</style>
